/**
 * MFA Service for Firebase Multi-Factor Authentication operations.
 *
 * This service wraps Firebase's MFA APIs and provides a clean interface
 * for TOTP and SMS-based MFA enrollment and verification.
 */
import {
  multiFactor,
  type MultiFactorResolver,
  TotpMultiFactorGenerator,
  TotpSecret,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  type MultiFactorInfo,
  type MultiFactorError,
  type ApplicationVerifier,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth'
import { auth } from './firebase'
import { firebaseErrorCode } from '@/utils/firebaseErrorCode'
import type { MfaChallenge, MfaProvider, SsoIdentity, TotpEnrollmentInfo } from './AuthProvider'

/** Firebase's provider ids, as the names the rest of the app calls those providers. */
const IDENTITY_PROVIDER_NAMES: Record<string, string> = {
  'google.com': 'google',
  'microsoft.com': 'microsoft',
}

export function identityProviderName(firebaseProviderId: string | null | undefined): string | undefined {
  return firebaseProviderId ? IDENTITY_PROVIDER_NAMES[firebaseProviderId] : undefined
}

/**
 * Service for managing MFA operations with Firebase.
 */
export class MfaService implements MfaProvider {
  // Store the TotpSecret internally to avoid type issues when passing it around
  private currentTotpSecret: TotpSecret | null = null
  /**
   * Checks if MFA is available for the current user.
   * Email must be verified before MFA can be enrolled.
   */
  async isMfaAvailable(): Promise<boolean> {
    const user = auth.currentUser
    if (!user) return false
    return user.emailVerified
  }

  /**
   * Gets the list of enrolled MFA factors for the current user.
   */
  async getEnrolledFactors(): Promise<MultiFactorInfo[]> {
    const user = auth.currentUser
    if (!user) return []

    const multiFactorUser = multiFactor(user)
    return multiFactorUser.enrolledFactors
  }

  /**
   * Checks if MFA is enabled for the current user.
   */
  async isMfaEnabled(): Promise<boolean> {
    const factors = await this.getEnrolledFactors()
    return factors.length > 0
  }

  // ==========================================================================
  // TOTP Enrollment (2 steps)
  // ==========================================================================

  /**
   * Starts TOTP enrollment by generating a secret.
   * Returns the secret and QR code URL for the authenticator app.
   *
   * Step 1 of 2 for TOTP enrollment.
   */
  async startTotpEnrollment(): Promise<TotpEnrollmentInfo> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    if (!user.emailVerified) {
      throw new Error('Email must be verified before enabling MFA')
    }

    const multiFactorUser = multiFactor(user)
    const session = await multiFactorUser.getSession()

    const totpSecret = await TotpMultiFactorGenerator.generateSecret(session)

    // Store the secret internally for use in completeTotpEnrollment
    this.currentTotpSecret = totpSecret

    // Generate QR code URL for authenticator apps
    const qrCodeUrl = totpSecret.generateQrCodeUrl(
      user.email || 'user',
      'Scrivio'
    )

    return {
      secret: totpSecret.secretKey,
      qrCodeUrl,
      secretKey: totpSecret.secretKey
    }
  }

  /**
   * Completes TOTP enrollment by verifying the code from the authenticator app.
   *
   * Step 2 of 2 for TOTP enrollment.
   * Uses the secret stored from startTotpEnrollment.
   *
   * @param verificationCode The 6-digit code from the authenticator app
   */
  async completeTotpEnrollment(verificationCode: string): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    if (!this.currentTotpSecret) {
      throw new Error('No TOTP enrollment in progress. Call startTotpEnrollment first.')
    }

    const multiFactorUser = multiFactor(user)

    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      this.currentTotpSecret,
      verificationCode
    )

    await multiFactorUser.enroll(assertion, 'Authenticator App')

    // Clear the stored secret after successful enrollment
    this.currentTotpSecret = null
  }

  /**
   * Clears any pending TOTP enrollment.
   * Call this if the user cancels the enrollment process.
   */
  clearPendingEnrollment(): void {
    this.currentTotpSecret = null
  }

  // ==========================================================================
  // SMS Enrollment (2 steps)
  // ==========================================================================

  /**
   * Starts SMS enrollment by sending a verification code to the phone number.
   *
   * Step 1 of 2 for SMS enrollment.
   *
   * @param phoneNumber The phone number in E.164 format (e.g., +31612345678)
   * @param recaptchaVerifier The reCAPTCHA verifier instance
   * @returns Verification ID to use with completeSmsEnrollment
   */
  async startSmsEnrollment(
    phoneNumber: string,
    recaptchaVerifier: ApplicationVerifier
  ): Promise<string> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    if (!user.emailVerified) {
      throw new Error('Email must be verified before enabling MFA')
    }

    const multiFactorUser = multiFactor(user)
    const session = await multiFactorUser.getSession()

    const phoneAuthProvider = new PhoneAuthProvider(auth)
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      {
        phoneNumber,
        session
      },
      recaptchaVerifier
    )

    return verificationId
  }

  /**
   * Completes SMS enrollment by verifying the code sent to the phone.
   *
   * Step 2 of 2 for SMS enrollment.
   *
   * @param verificationId The verification ID from startSmsEnrollment
   * @param verificationCode The 6-digit code from SMS
   */
  async completeSmsEnrollment(
    verificationId: string,
    verificationCode: string
  ): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    const multiFactorUser = multiFactor(user)

    const credential = PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    )
    const assertion = PhoneMultiFactorGenerator.assertion(credential)

    await multiFactorUser.enroll(assertion, 'Phone Number')
  }

  // ==========================================================================
  // MFA Challenge Handling (during sign-in)
  // ==========================================================================

  /**
   * Checks if an error indicates that MFA verification is required.
   */
  isMfaRequiredError(error: unknown): error is MultiFactorError {
    return firebaseErrorCode(error) === 'auth/multi-factor-auth-required'
  }

  /**
   * Gets the MFA resolver from an MFA required error.
   */
  getMfaResolver(error: unknown): MultiFactorResolver {
    return getMultiFactorResolver(auth, error as MultiFactorError)
  }

  /**
   * Firebase's own `signInWithIdp` answer is the only place the person is named before the second
   * factor: the error has no user and the resolver's hints are factors. `customData._serverResponse`
   * is that response as the server sent it, which is also what {@link getMfaResolver} reads.
   */
  getMfaRequiredIdentity(error: unknown): SsoIdentity {
    const { customData } = error as { customData?: { _serverResponse?: { email?: string; providerId?: string } } }
    const response = customData?._serverResponse
    return { email: response?.email ?? null, identityProvider: identityProviderName(response?.providerId) }
  }

  /**
   * Handles MFA challenge with TOTP code.
   *
   * @param challenge The Firebase resolver {@link getMfaResolver} returned
   * @param factorIndex The index of the factor to use (usually 0)
   * @param verificationCode The 6-digit code from the authenticator app
   */
  async handleTotpChallenge(
    challenge: MfaChallenge,
    factorIndex: number,
    verificationCode: string
  ): Promise<void> {
    const resolver = challenge as MultiFactorResolver
    const hint = resolver.hints[factorIndex]
    if (!hint) {
      throw new Error('Invalid factor index')
    }

    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
      hint.uid,
      verificationCode
    )

    await resolver.resolveSignIn(assertion)
  }

  /**
   * Handles MFA challenge with SMS code.
   *
   * @param resolver The MFA resolver from the sign-in error
   * @param factorIndex The index of the factor to use
   * @param verificationId The verification ID from sending the SMS
   * @param verificationCode The 6-digit code from SMS
   */
  async handleSmsChallenge(
    resolver: MultiFactorResolver,
    verificationId: string,
    verificationCode: string
  ): Promise<void> {
    const credential = PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    )
    const assertion = PhoneMultiFactorGenerator.assertion(credential)

    await resolver.resolveSignIn(assertion)
  }

  /**
   * Sends SMS verification code for MFA challenge.
   *
   * @param resolver The MFA resolver from the sign-in error
   * @param factorIndex The index of the phone factor to use
   * @param recaptchaVerifier The reCAPTCHA verifier instance
   * @returns Verification ID to use with handleSmsChallenge
   */
  async sendSmsVerification(
    resolver: MultiFactorResolver,
    factorIndex: number,
    recaptchaVerifier: ApplicationVerifier
  ): Promise<string> {
    const hint = resolver.hints[factorIndex]
    const phoneAuthProvider = new PhoneAuthProvider(auth)

    return await phoneAuthProvider.verifyPhoneNumber(
      {
        multiFactorHint: hint,
        session: resolver.session
      },
      recaptchaVerifier
    )
  }

  // ==========================================================================
  // Unenroll (Disable MFA)
  // ==========================================================================

  /**
   * Unenrolls (removes) an MFA factor.
   *
   * @param factorUid The UID of the factor to remove
   */
  async unenrollFactor(factorUid: string): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    const multiFactorUser = multiFactor(user)
    await multiFactorUser.unenroll(factorUid)
  }

  /**
   * Unenrolls all MFA factors (completely disables MFA).
   */
  async unenrollAllFactors(): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    const multiFactorUser = multiFactor(user)
    const factors = multiFactorUser.enrolledFactors

    for (const factor of factors) {
      await multiFactorUser.unenroll(factor.uid)
    }
  }

  // ==========================================================================
  // Re-authentication (for security-sensitive operations)
  // ==========================================================================

  /**
   * Checks if an error indicates that a recent login is required.
   * This typically happens when trying to enroll MFA on a stale session.
   */
  isRecentLoginRequiredError(error: unknown): boolean {
    return firebaseErrorCode(error) === 'auth/requires-recent-login'
  }

  /**
   * Re-authenticates the user with email and password.
   * Required before security-sensitive operations like MFA enrollment.
   *
   * @param password The user's current password
   */
  async reauthenticateWithPassword(password: string): Promise<void> {
    const user = auth.currentUser
    if (!user || !user.email) {
      throw new Error('No authenticated user with email')
    }

    const credential = EmailAuthProvider.credential(user.email, password)
    await reauthenticateWithCredential(user, credential)
  }

  /**
   * Re-authenticates the user with Google SSO popup.
   * Required before security-sensitive operations like MFA enrollment.
   */
  async reauthenticateWithGoogle(): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    const provider = new GoogleAuthProvider()
    await reauthenticateWithPopup(user, provider)
  }

  /**
   * Re-authenticates the user with Microsoft SSO popup.
   * Required before security-sensitive operations like MFA enrollment.
   */
  async reauthenticateWithMicrosoft(): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    const provider = new OAuthProvider('microsoft.com')
    provider.addScope('openid')
    provider.addScope('email')
    provider.addScope('profile')
    await reauthenticateWithPopup(user, provider)
  }

  /**
   * Determines the authentication provider used by the current user.
   * Returns 'password', 'google.com', 'microsoft.com', or null if unknown.
   */
  getCurrentAuthProvider(): string | null {
    const user = auth.currentUser
    if (!user) return null

    const providerData = user.providerData
    const firstProvider = providerData[0]
    if (!firstProvider) return null

    // Return the first provider's providerId
    return firstProvider.providerId
  }
}

// Export a singleton instance
export const mfaService = new MfaService()
